import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { API_BASE } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useNotifications } from '../contexts/NotificationsContext';
import { getNotificationTarget, type NotificationTarget } from '../utils/notification-links';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { cn } from '@/lib/utils';
import { CardMetaText } from './CardMetaText';
import { NotificationMessage } from './NotificationMessage';

interface DeskNotification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  readAt: Date | string | null;
  metadata: Record<string, unknown> | null;
  createdAt: Date | string;
}

function formatRelativeDate(date: Date | string, t: (key: string) => string): string {
  const now = new Date();
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const diff = now.getTime() - dateObj.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) {
    return t('just_now');
  }
  if (minutes < 60) {
    return `${minutes} ${minutes === 1 ? t('minute_ago') : t('minutes_ago')}`;
  }
  if (hours < 24) {
    return `${hours} ${hours === 1 ? t('hour_ago') : t('hours_ago')}`;
  }
  if (days < 7) {
    return `${days} ${days === 1 ? t('day_ago') : t('days_ago')}`;
  }
  return dateObj.toLocaleDateString();
}

export const DeskNotifications: React.FC = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { t } = useLocale();
  const { markAsRead, unreadCount } = useNotifications();
  const [notifications, setNotifications] = useState<DeskNotification[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async (): Promise<void> => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/notifications`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const data = await response.json();
      setNotifications(data.notifications || []);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (unreadCount > 0) {
      return;
    }
    setNotifications((prev) =>
      prev.some((notification) => !notification.read)
        ? prev.map((notification) =>
            notification.read
              ? notification
              : { ...notification, read: true, readAt: new Date() }
          )
        : prev
    );
  }, [unreadCount]);

  const openTarget = (target: NotificationTarget): void => {
    if (target.external) {
      window.open(target.href, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(target.href);
  };

  const openNotificationTarget = (
    type: string,
    metadata: Record<string, unknown> | null
  ): void => {
    const target = getNotificationTarget(type, metadata, { isAdmin: Boolean(user?.admin) });
    if (!target) {
      return;
    }
    openTarget(target);
  };

  const handleNotificationClick = async (notification: DeskNotification): Promise<void> => {
    if (!notification.read) {
      await markAsRead(notification.id);
      await fetchNotifications();
    }
    openNotificationTarget(notification.type, notification.metadata);
  };

  return (
    <>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
          <div>{t('loading')}</div>
        </div>
      ) : notifications.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
          <p style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
            {t('no_notifications')}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {notifications.map((notification) => {
            const target = getNotificationTarget(
              notification.type,
              notification.metadata,
              { isAdmin: Boolean(user?.admin) }
            );
            return (
              <Card
                key={notification.id}
                className={cn(
                  'relative border-0 shadow-none rounded-[10px] transition-all hover:bg-card',
                  target && 'cursor-pointer',
                )}
                style={{ border: '1px solid var(--border)' }}
                onClick={() => handleNotificationClick(notification)}
              >
                <div style={{ padding: '1.5rem 2rem' }}>
                  <div
                    style={{
                      flex: 1,
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: notification.read ? '500' : '600', marginBottom: '0.25rem' }}>
                            {notification.title}
                          </h3>
                          <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                            <NotificationMessage
                              message={notification.message}
                              type={notification.type}
                              metadata={notification.metadata}
                              isAdmin={Boolean(user?.admin)}
                              onOpenTarget={openTarget}
                            />
                          </p>
                          <CardMetaText className="mt-0">
                            {formatRelativeDate(notification.createdAt, t)}
                          </CardMetaText>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                          {user?.admin &&
                            notification.type === 'user_feedback_submitted' &&
                            typeof notification.metadata?.feedbackId === 'string' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/admin/user-feedback?feedbackId=${encodeURIComponent(String(notification.metadata?.feedbackId))}`
                                  );
                                }}
                                aria-label={t('user_feedback')}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            )}
                          {!notification.read && (
                            <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </>
  );
};
